import { KING, PAWN, typeFromLetter, typeLetter, type PromotionPiece } from '../primitives/piece.js'
import { parseSquare, squareName } from '../primitives/square.js'
import { matchIntent, type Move } from '../position/move.js'
import type { Position } from '../position/position.js'
import { generateLegalMoves } from '../movegen/generate.js'
import { err, ok, type Result } from '../result.js'

export type UciError =
  | { readonly kind: 'malformed'; readonly text: string }
  | { readonly kind: 'illegal'; readonly text: string }

/** `e2e4`, `e7e8q`. Castling is encoded as the king's two squares, not `O-O`. */
export const moveToUci = (move: Move): string => {
  const base = squareName(move.from) + squareName(move.to)
  return move.kind === 'promotion' ? base + typeLetter(move.promoteTo).toLowerCase() : base
}

export const parseUci = (pos: Position, text: string): Result<Move, UciError> => {
  if (text.length !== 4 && text.length !== 5) return err({ kind: 'malformed', text })

  const from = parseSquare(text.slice(0, 2))
  const to = parseSquare(text.slice(2, 4))
  if (from === null || to === null) return err({ kind: 'malformed', text })

  let promoteTo: PromotionPiece | undefined
  if (text.length === 5) {
    const type = typeFromLetter(text[4] as string)
    if (type === null || type === PAWN || type === KING) return err({ kind: 'malformed', text })
    promoteTo = type
  }

  const move = matchIntent(
    generateLegalMoves(pos),
    promoteTo === undefined ? { from, to } : { from, to, promoteTo },
  )
  return move === null ? err({ kind: 'illegal', text }) : ok(move)
}
